import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Box, Typography, Grid, Avatar, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { useParams } from "react-router-dom";
import { SOCKET } from "../../../../constants/Bidding/BiddingConstants";
import "../../../../assets/styles/Bidding/MainScreenOverlay.css";
import { BACKEND_DOMAIN } from "../../../../common/env";
import { useWindowSize } from "react-use";
import Confetti from "react-confetti";

const AuctionMainScreenOverlay = () => {
    const { auctionId } = useParams();
    const { width, height } = useWindowSize();
    
    const [auctionName, setAuctionName] = useState("");
    const [auctionDate, setAuctionDate] = useState("");
    const [randomPlayerToBid, setRandomPlayerToBid] = useState(null);
    const [bidAmount, setBidAmount] = useState(0);
    const [selectedTeam, setSelectedTeam] = useState("");
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isBidFinalized, setIsBidFinalized] = useState(false);
    const [finalizedDetails, setFinalizedDetails] = useState(null);
    const [isFlipping, setIsFlipping] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);
    const flipTimeoutRef = useRef(null);

    // Clear any existing timeout when component unmounts
    useEffect(() => {
        return () => {
            if (flipTimeoutRef.current) {
                clearTimeout(flipTimeoutRef.current);
            }
        };
    }, []);

    // Fetch initial auction data
    useEffect(() => {
        const fetchAuctionData = async () => {
            try {
                // Fetch auction details
                const auctionResponse = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}`);
                setAuctionName(auctionResponse.data.title || "Auction");
                setAuctionDate(auctionResponse.data.date || "");

                // Fetch teams data
                const teamsResponse = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/teams`);
                setTeams(teamsResponse.data.teams);
                
                setLoading(false);
            } catch (error) {
                console.error("Error fetching auction data:", error);
                setLoading(false);
            }
        };

        fetchAuctionData();

        // Join auction room
        SOCKET.emit("join-auction", { auctionId });

        // Listen for current state events (when joining auction)
        const handleCurrentState = (data) => {
            if (data.auctionId === auctionId) {
                if (data.randomPlayerToBid !== undefined) {
                    setRandomPlayerToBid(data.randomPlayerToBid || null);
                }
                if (data.selectedTeam !== undefined) {
                    setSelectedTeam(data.selectedTeam || "");
                }
                if (data.bidAmount !== undefined) {
                    setBidAmount(data.bidAmount || 0);
                }
            }
        };

        // Listen for player selection events
        const handlePlayerSelected = (data) => {
            if (data.auctionId === auctionId) {
                setRandomPlayerToBid(data.player);
                setBidAmount(data.player?.baseValue || 0);
                setSelectedTeam("");
                // Removed setIsFlipping(true) to prevent coin flip after player selection
                // The coin flip should only happen once before player selection
            }
        };

        // Listen for bid amount change events
        const handleBidAmountChanged = (data) => {
            if (data.auctionId === auctionId) {
                setBidAmount(data.bidAmount);
            }
        };

        // Listen for team selection events
        const handleTeamSelected = (data) => {
            if (data.auctionId === auctionId) {
                setSelectedTeam(data.teamName);
            }
        };

        // Listen for reset bidding events
        const handleResetBidding = (data) => {
            if (data.auctionId === auctionId) {
                setRandomPlayerToBid(null);
                setSelectedTeam("");
                setBidAmount(0);
            }
        };

        // Listen for bid finalized events
        const handleBidFinalized = (data) => {
            if (data.auctionId === auctionId) {
                setRandomPlayerToBid(null);
                setSelectedTeam("");
                setBidAmount(0);
                setFinalizedDetails(data.finalizedDetails);
                setIsBidFinalized(true);
                setShowCelebration(true);
                // Hide celebration after 5 seconds
                setTimeout(() => {
                    setIsBidFinalized(false);
                    setShowCelebration(false);
                }, 5000);
            }
        };

        // Listen for flipping coin test events
        const handleFlippingCoinTest = (data) => {
            if (data.auctionId === auctionId) {
                // Clear any existing timeout
                if (flipTimeoutRef.current) {
                    clearTimeout(flipTimeoutRef.current);
                }
                
                setIsFlipping(true);
                // Hide coin flip after 3 seconds
                flipTimeoutRef.current = setTimeout(() => {
                    setIsFlipping(false);
                    flipTimeoutRef.current = null;
                }, 3000);
            }
        };

        // Listen for player pass events
        const handlePlayerPass = (data) => {
            if (data.auctionId === auctionId) {
                setRandomPlayerToBid(null);
                setSelectedTeam("");
                setBidAmount(0);
            }
        };

        // Register all socket listeners
        SOCKET.on('current-state', handleCurrentState);
        SOCKET.on('player-selected', handlePlayerSelected);
        SOCKET.on('bid-amount-changed', handleBidAmountChanged);
        SOCKET.on('team-selected', handleTeamSelected);
        SOCKET.on('reset-bidding', handleResetBidding);
        SOCKET.on('bid-finalized', handleBidFinalized);
        SOCKET.on('flipping-coin-test', handleFlippingCoinTest);
        SOCKET.on('player-pass', handlePlayerPass);

        // Cleanup socket listeners
        return () => {
            // Clear any existing timeout
            if (flipTimeoutRef.current) {
                clearTimeout(flipTimeoutRef.current);
            }
            
            SOCKET.off('current-state', handleCurrentState);
            SOCKET.off('player-selected', handlePlayerSelected);
            SOCKET.off('bid-amount-changed', handleBidAmountChanged);
            SOCKET.off('team-selected', handleTeamSelected);
            SOCKET.off('reset-bidding', handleResetBidding);
            SOCKET.off('bid-finalized', handleBidFinalized);
            SOCKET.off('flipping-coin-test', handleFlippingCoinTest);
            SOCKET.off('player-pass', handlePlayerPass);
        };
    }, [auctionId]);

    if (loading) {
        return (
            <Box 
                sx={{ 
                    width: "100vw", 
                    height: "100vh", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    backgroundColor: "#000",
                    color: "#fff"
                }}
            >
                <Typography variant="h4">Loading auction data...</Typography>
            </Box>
        );
    }

    return (
        <div className="main-screen-overlay">
            {/* Auction Title Header */}
            <div className="main-screen-overlay-header">
                <Typography variant="h1" className="main-screen-overlay-title">
                    {auctionName}
                </Typography>
            </div>
            
            {/* Auction Description */}
            
            {/* Current Bid - Centered at Top */}
            {randomPlayerToBid && (
                <div className="main-screen-overlay-current-bid-top">
                    <Typography variant="h1" className="main-screen-overlay-bid-amount-top">
                        Top Bid: <span className="main-screen-overlay-bid-highlight-top">${bidAmount}</span>
                    </Typography>
                </div>
            )}
            
            {/* Coin Flip Animation */}
            {isFlipping && (
                <Box
                    className = "coin-flip-overlay"
                >
                    <Box
                        className="coin-flip-wrapper"
                        sx={{animation: 'flip 1.2s linear infinite'}}
                    >
                
                        {/* Front Side of the Coin */}
                        <Box
                            component="img"
                            src="/images/CPL Logo.png"
                            alt="Cricket Bat"
                            className="coin-front"
                        />

                        {/* Back Side of the Coin */}
                        <Box
                            className="coin-back"
                            component="img"
                            src="/images/Edmonton_Chargers_logo.png"
                            alt="Cricket Bat"
                        />
                    
                    </Box>
                    <style>
                        {`
                            @keyframes flip {
                                0% { transform: rotateY(0); }
                                100% { transform: rotateY(360deg); }
                            }
                        `}
                    </style>
                </Box>
            )}

            {/* Celebration Animation */}
            {showCelebration && (
                <Box 
                    sx={{ 
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                >
                    {isBidFinalized && <Confetti width={width} height={height} />}
                    <Typography 
                        variant="h2" 
                        sx={{ 
                            color: '#fff',
                            mb: 4,
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '3rem'
                        }}
                    >
                        🎉 Congratulations! 🎉
                    </Typography>
                    {finalizedDetails && (
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 4, mb: 4 }}>
                            {finalizedDetails.playerLogo && (
                                <Avatar 
                                    src={finalizedDetails.playerLogo}
                                    alt={finalizedDetails.playerName}
                                    sx={{ width: { xs: 120, sm: 150, md: 180 }, height: { xs: 120, sm: 150, md: 180 }, border: '4px solid #fff', boxShadow: '0 0 20px rgba(255, 255, 255, 0.5)' }}
                                />
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography 
                                    variant="h3" 
                                    sx={{ 
                                        color: '#fff',
                                        mb: 2,
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' }
                                    }}
                                >
                                    {finalizedDetails.playerName}
                                </Typography>
                                <Typography 
                                    variant="h4" 
                                    sx={{ 
                                        color: '#4caf50',
                                        fontWeight: 'bold',
                                        mb: 2,
                                        fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }
                                    }}
                                >
                                    SOLD TO
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                    {finalizedDetails.teamLogo && (
                                        <Avatar 
                                            src={finalizedDetails.teamLogo}
                                            alt={finalizedDetails.teamName}
                                            sx={{ width: { xs: 80, sm: 100, md: 120 }, height: { xs: 80, sm: 100, md: 120 }, border: '3px solid #fff', boxShadow: '0 0 15px rgba(255, 255, 255, 0.5)' }}
                                        />
                                    )}
                                    <Typography 
                                        variant="h2" 
                                        sx={{ 
                                            color: '#2196f3',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' }
                                        }}
                                    >
                                        {finalizedDetails.teamName}
                                    </Typography>
                                </Box>
                                <Typography 
                                    variant="h1" 
                                    sx={{ 
                                        color: '#ffeb3b',
                                        fontWeight: 'bold',
                                        mt: 3,
                                        fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                                        textShadow: '0 0 10px rgba(255, 235, 59, 0.7)'
                                    }}
                                >
                                    ${finalizedDetails.bidAmount}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        mt: 3,
                        animation: 'confetti 2s infinite'
                    }}>
                        {[...Array(20)].map((_, i) => (
                            <Box 
                                key={i}
                                sx={{ 
                                    width: 10, 
                                    height: 10, 
                                    backgroundColor: ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722'][i % 16],
                                    borderRadius: '50%',
                                    animation: `fall ${Math.random() * 3 + 2}s linear infinite`,
                                    animationDelay: `${Math.random() * 2}s`
                                }}
                            />
                        ))}
                    </Box>
                    <style>
                        {`
                            @keyframes confetti {
                                0% { transform: translateY(0) rotate(0); }
                                100% { transform: translateY(-20px) rotate(360deg); }
                            }
                            @keyframes fall {
                                0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
                                100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
                            }
                        `}
                    </style>
                </Box>
            )}

            {/* Main Content */}
            <div className="main-screen-overlay-content">
                <Grid container spacing={3} className="main-screen-overlay-grid">
                    {/* Left-hand side: Player Details */}
                    <Grid item xs={12} md={6} className="main-screen-overlay-grid-item">
                        <div className="main-screen-overlay-player-section">
                            {randomPlayerToBid ? (
                                <>
                                    <Avatar
                                        src={`${randomPlayerToBid.playerLogo}`}
                                        alt={randomPlayerToBid.playerName}
                                        className="player-avatar main-screen-overlay-avatar"
                                        sx={{ 
                                            width: 140, 
                                            height: 140,
                                            margin: '0 auto 15px auto',
                                            border: '3px solid #fff',
                                            boxShadow: '0 0 15px rgba(0, 0, 0, 0.3)'
                                        }}
                                    />
                                    <Typography variant="h2" className="player-name main-screen-overlay-player-name" sx={{ 
                                        textAlign: 'center',
                                        fontSize: '1.8rem',
                                        mb: 1
                                    }}>
                                        {randomPlayerToBid.playerName}
                                    </Typography>
                                    <Typography variant="h4" className="player-type main-screen-overlay-player-type" sx={{ 
                                        textAlign: 'center',
                                        fontSize: '1.2rem',
                                        mb: 1,
                                        color: '#666'
                                    }}>
                                        {randomPlayerToBid.playerType}
                                    </Typography>
                                    {/* <Typography variant="h4" className="player-base-value main-screen-overlay-base-value" sx={{ 
                                        textAlign: 'center',
                                        fontSize: '1.2rem',
                                        mb: 2
                                    }}>
                                        Base Value:{" "}
                                        <span className="base-value-amount">{`$${randomPlayerToBid.baseValue}`}</span>
                                    </Typography> */}
                                    <Typography></Typography>

                                    {/* Player Stats Table - Adjusted to fit screen */}
                                    <div className="main-screen-overlay-stats-container" style={{ 
                                        maxHeight: '200px', 
                                        overflowY: 'auto',
                                        marginTop: '10px'
                                    }}>
                                        <Table className="player-stats-table main-screen-overlay-stats-table" size="small">
                                            <TableHead>
                                                <TableRow className="main-screen-overlay-stats-header">
                                                    <TableCell className="player-stats-cell main-screen-overlay-stats-cell-header" sx={{ 
                                                        fontWeight: 'bold',
                                                        fontSize: '0.9rem',
                                                        padding: '8px 4px',
                                                        textAlign: 'center'
                                                    }}>Matches</TableCell>
                                                    <TableCell className="player-stats-cell main-screen-overlay-stats-cell-header" sx={{ 
                                                        fontWeight: 'bold',
                                                        fontSize: '0.9rem',
                                                        padding: '8px 4px',
                                                        textAlign: 'center'
                                                    }}>Runs</TableCell>
                                                    <TableCell className="player-stats-cell main-screen-overlay-stats-cell-header" sx={{ 
                                                        fontWeight: 'bold',
                                                        fontSize: '0.9rem',
                                                        padding: '8px 4px',
                                                        textAlign: 'center'
                                                    }}>Wickets</TableCell>
                                                    
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow className="main-screen-overlay-stats-row">
                                                    <TableCell className="player-stats-cell main-screen-overlay-stats-cell" sx={{ 
                                                        fontSize: '1rem',
                                                        padding: '8px 4px',
                                                        textAlign: 'center'
                                                    }}>{randomPlayerToBid.stats?.matches || 0}</TableCell>
                                                    <TableCell className="player-stats-cell main-screen-overlay-stats-cell" sx={{ 
                                                        fontSize: '1rem',
                                                        padding: '8px 4px',
                                                        textAlign: 'center'
                                                    }}>{randomPlayerToBid.stats?.runs || 0}</TableCell>
                                                    <TableCell className="player-stats-cell main-screen-overlay-stats-cell" sx={{ 
                                                        fontSize: '1rem',
                                                        padding: '8px 4px',
                                                        textAlign: 'center'
                                                    }}>{randomPlayerToBid.stats?.wickets || 0}</TableCell>
                                                    
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </div>
                                </>
                            ) : (
                                <Typography variant="body1" className="no-player-message" sx={{ 
                                    textAlign: 'center',
                                    fontSize: '1.2rem',
                                    padding: '40px 20px'
                                }}>
                                    No player selected yet.
                                </Typography>
                            )}
                        </div>
                    </Grid>

                    {/* Right-hand side: Team Details */}
                    <Grid item xs={12} md={6} className="main-screen-overlay-grid-item">
                        <div className="main-screen-overlay-team-section">
                            <div className="main-screen-overlay-team-info">
                                <Avatar
                                    src={`${teams.find((team) => team.teamName === selectedTeam)?.teamLogo}`}
                                    alt={selectedTeam}
                                    className="team-avatar main-screen-overlay-team-avatar"
                                    sx={{ 
                                        width: 140, 
                                        height: 140,
                                        margin: '0 auto 15px auto',
                                        border: '3px solid #fff',
                                        boxShadow: '0 0 15px rgba(0, 0, 0, 0.3)'
                                    }}
                                />
                                <Typography variant="h3" className="team-name main-screen-overlay-team-name" sx={{ 
                                    textAlign: 'center',
                                    fontSize: '1.6rem'
                                }}>
                                    {selectedTeam || "No Team Selected"}
                                </Typography>
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default AuctionMainScreenOverlay;